
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    console.error("ErrorBoundary caught an error:", error.message);
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to the console for debugging
    console.error("ErrorBoundary componentDidCatch:", error);
    console.error("Component stack:", errorInfo.componentStack);
    
    this.setState({ errorInfo });
    
    // You could also send this to an error reporting service
    // reportErrorToService(error, errorInfo);
  }

  private resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  private clearDataAndRestart = () => {
    try {
      // Clear any stored auth data that might be corrupted
      localStorage.clear();
      console.log("All localStorage data cleared");
      window.location.href = '/';
    } catch (e) {
      console.error("Failed to clear localStorage:", e);
      // Force a hard reload even if localStorage clear failed
      window.location.href = '/';
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="bg-red-100 p-6 rounded-lg max-w-md text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
            <p className="mb-4 text-gray-700">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            
            {this.state.errorInfo && (
              <details className="mb-4 text-left text-sm bg-gray-50 p-2 rounded">
                <summary className="cursor-pointer font-medium mb-1">Technical details</summary>
                <pre className="whitespace-pre-wrap overflow-auto max-h-48 text-xs">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            <div className="space-y-4">
              <Button 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Reload page
              </Button>
              
              <Button 
                variant="outline" 
                onClick={this.resetError}
                className="w-full"
              >
                Try to recover
              </Button>
              
              <Button 
                variant="destructive" 
                onClick={this.clearDataAndRestart}
                className="w-full"
              >
                Clear data & restart
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
